/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import {
    AddToCartBehavior,
    IAddToCartFailureResult,
    IAddToCartResources,
    ItemSuccessfullyAddedToCartNotification,
    PriceComponent
} from '@msdyn365-commerce/components';
import { getUrlSync, ICoreContext, IImageSettings } from '@msdyn365-commerce/core';
import { ICartActionResult, getCartState } from '@msdyn365-commerce/global-state';
import { DeliveryOption, FeatureState, OrgUnitLocation, ProductAvailableQuantity, ProductDimension } from '@msdyn365-commerce/retail-proxy';
import { NotificationsManager } from '@msdyn365-commerce-modules/notifications-core';
import {
    ArrayExtensions,
    DeliveryMode,
    IProductInventoryInformation
    // isAggregatedInventory
} from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, INodeProps, IPopupProps, Popup, format } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { BuyboxErrorBlock } from './buybox-components';
import { IBuyboxCallbacks, IBuyboxState } from '@msdyn365-commerce-modules/buybox';
import { getConfigureErrors } from './utilities/error-utilities';
// import { getGenericError } from '@msdyn365-commerce-modules/buybox';
import { ILdsBuyboxData } from '../lds-buybox.data';
import { ILdsBuyboxProps, ILdsBuyboxResources } from '../lds-buybox.props.autogenerated';
//@ts-ignore
import { Primary } from '@churchofjesuschrist/eden-buttons';
//@ts-ignore
import { Error, Modal } from '@churchofjesuschrist/eden-alert';
import { getGenericError } from './utilities/error-utilities';

const modalCloseButtonReference: React.RefObject<HTMLButtonElement> = React.createRef();

/**
 * FindinStore failure Interface.
 */
export interface IFindInStoreFailureResult {
    missingDimensions?: ProductDimension[];
}

/**
 * BuyBoxFindinStore View Props Interface.
 */
export interface IBuyboxFindInStoreViewProps {
    storeSelector?: React.ReactNode;
    heading?: React.ReactNode;
    description?: React.ReactNode;
    errors?: React.ReactNode;
    button?: React.ReactNode;
    productPickupOptionList?: React.ReactNode;

    modal?: React.ReactNode;

    addToCartValidationError?: any;
    addToCartQuantityError?: any;

    ContainerProps: INodeProps;

    openFindInStoreDialog(): Promise<void>;
}

/**
 * On click find store handler.
 * @param props -Buybox data.
 * @param state -Buybox state.
 * @param callbacks -Buybox callbacks.
 * @returns Find in store.
 */
const onClickFindInStoreHandler = (
    props: ILdsBuyboxProps<ILdsBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    hideButton: any,
    errorLabels?: any,
    customStyleError?: any,
    customColorError?: any,
    setValidationError?: any,
    setQuantityError?: any
) => async () => {
    let dataProduct = props.data.product.result;
    if (state.selectedProduct) {
        dataProduct = (await state.selectedProduct) || dataProduct;
    }

    const product = dataProduct;

    let missingDimensions;
    if (product) {
        missingDimensions =
            product.Dimensions &&
            product.Dimensions.filter((dimension: ProductDimension) => !(dimension.DimensionValue && dimension.DimensionValue.Value));
    }
    if (
        (await props.data.cart).cart &&
        (await props.data.cart).cart.CartLines &&
        //@ts-ignore
        (await props.data.cart).cart.CartLines[0]
    ) {
        if (
            //@ts-ignore
            (await props.data.cart).cart.CartLines[0].DeliveryMode === 'Pickup' ||
            //@ts-ignore
            (await props.data.cart).cart.CartLines[0].DeliveryMode === 'PICKUP' ||
            //@ts-ignore
            (await props.data.cart).cart.CartLines[0].DeliveryMode === 'CURBSIDE' ||
            //@ts-ignore
            (await props.data.cart).cart.CartLines[0].DeliveryMode === 'Curbside' ||
            (missingDimensions && missingDimensions.length > 0)
        ) {
            await findInStoreOnClick(
                props,
                state,
                callbacks,
                hideButton,
                errorLabels,
                customStyleError,
                customColorError,
                setValidationError,
                setQuantityError
            );
        } else {
            callbacks.updateErrorState({
                errorHost: 'FINDINSTORE',
                configureErrors: getConfigureErrors(
                    undefined,
                    props.resources,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    props.resources.shippingCartError
                )
            });
        }
    } else {
        callbacks.updateErrorState({
            errorHost: 'FINDINSTORE',
            configureErrors: getConfigureErrors(undefined, props.resources, undefined, undefined, undefined, undefined, false)
        });
        await findInStoreOnClick(
            props,
            state,
            callbacks,
            hideButton,
            errorLabels,
            customStyleError,
            customColorError,
            setValidationError,
            setQuantityError
        );
    }
};

/**
 * Inventory settings based on the inventory policy for aggregated availability.
 */
interface IInventorySettingsProps {
    isSectionDisabled: boolean;
    description: string;
    buttonText: string;
}

/**
 * The function defines the settings values for a given inventory configuration.
 * @param context - The context.
 * @param isStockCheck - Flag to enable inventory validation.
 * @param productAvailability - The product inventory information.
 * @param buyboxResources - The buybox string resources.
 * @param isLoading - Is loading.
 * @returns Boolean.
 */
function getFindInStoreInventorySettings(
    context: ICoreContext,
    isStockCheck: boolean,
    productAvailability: IProductInventoryInformation[] | undefined,
    buyboxResources: ILdsBuyboxResources,
    isLoading?: boolean
): IInventorySettingsProps {
    // If shouldValidateAggregation is true then is required to check for the product inventory, based on this the messaging will change
    // and the find in store button will be ensbled or disabled

    let isSectionDisabled: boolean;
    let buttonText;
    let description: string;

    let pickupInventory: IProductInventoryInformation | undefined;

    if (!isStockCheck || isLoading) {
        return {
            isSectionDisabled: false,
            buttonText: buyboxResources.findInStoreLinkText,
            description: buyboxResources.findInStoreDescriptionText
        };
    }

    if (productAvailability) {
        pickupInventory = productAvailability.find(pickup => pickup.deliveryType === DeliveryMode.pickup);
    }

    // If aggregated is enabled, then we need to validate the inventor check for find in store.
    // If an individual is selected, then we will always show a find in store button.

    // if (isAggregatedInventory(context.actionContext)) {

    if (productAvailability && pickupInventory?.IsProductAvailable) {
        isSectionDisabled = false;
        buttonText = buyboxResources.findInStoreLinkText;
        description = buyboxResources.findInStoreDescriptionText;
    } else {
        isSectionDisabled = true;
        buttonText = buyboxResources.findInStoreNotAvailableTextButton;
        description = buyboxResources.findInStoreNotAvailableDescription;
    }
    // } else {
    //     isSectionDisabled = false;
    //     buttonText = buyboxResources.findInStoreLinkText;
    //     description = buyboxResources.findInStoreDescriptionText;
    // }

    return {
        isSectionDisabled,
        description,
        buttonText
    };
}

/**
 * Method to check if we have atleast one common delivery code between product and channel.
 * @param productdeliveryOptions - Product DeliveryOption List.
 * @param storePickUpOptionList - Channel DeliveryOption List.
 * @returns Boolean flag.
 */
const matchDeliveryOptions = (
    productdeliveryOptions: DeliveryOption[] | undefined,
    storePickUpOptionList: string[] | undefined
): boolean => {
    const deliveryOption: string[] = [];
    productdeliveryOptions?.map(delivery => {
        const pickup = storePickUpOptionList?.find(deliveryCode => deliveryCode === delivery.Code);
        if (pickup) {
            deliveryOption.push(pickup);
        }
        return deliveryOption;
    });

    return ArrayExtensions.hasElements(deliveryOption);
};

export function getBuyboxFindInStore(
    props: ILdsBuyboxProps<ILdsBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    hideButton: any,
    showAddToCart: boolean,
    dropdownLabels?: any,
    setValidationError?: any,
    setQuantityError?: any,
    max?: number,
    quantityErrorMessage?: string
): IBuyboxFindInStoreViewProps | undefined {
    const {
        data,
        slots: { storeSelector },
        resources: {
            priceFree,
            originalPriceText,
            currentPriceText,
            // findInStoreHeaderText,
            // findInStoreDescriptionText,
            buyBoxGoToCartText,
            buyBoxContinueShoppingText,
            closeNotificationLabel,
            buyBoxSingleItemText,
            buyBoxMultiLineItemFormatText,
            buyBoxHeaderMessageText,
            descriptionTextToShowAllPickupOptions
        },
        context: {
            request: { channel: { PickupDeliveryModeCode } = { PickupDeliveryModeCode: undefined } }
        }
    } = props;

    const customStyleError = dropdownLabels
        ? dropdownLabels?.find((x: any) => x.ExtensionProperties?.find((y: any) => y.Key === 'AttributeType')?.Value?.IntegerValue === 5)
        : null;

    const customColorError = dropdownLabels
        ? dropdownLabels?.find((x: any) => x.ExtensionProperties?.find((y: any) => y.Key === 'AttributeType')?.Value?.IntegerValue === 7)
        : null;
    const configError = state.errorState.configureErrors[2];
    const sizeError = state.errorState.configureErrors[3];

    const customError =
        customColorError && customStyleError
            ? {
                  1: `${customColorError.TextValue} ${props.resources.productDimensionTypeColorErrorMessage} ${customColorError.TextValue}`,
                  2: configError,
                  3: sizeError,
                  4: `${customStyleError.TextValue} ${props.resources.productDimensionTypeSizeErrorMessage} ${customStyleError.TextValue}`
              }
            : null;

    const errorLabels =
        customStyleError && customColorError
            ? [customStyleError, customColorError]
            : customColorError && !customStyleError
            ? [customColorError]
            : customStyleError && !customColorError
            ? [customStyleError]
            : null;

    const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
    const product = data?.product?.result;
    const price = data?.productPrice?.result;
    const storeSelectorStateManager = data?.storeSelectorStateManager?.result;
    const cart = data?.cart?.result;
    const deliveryOptions = data?.deliveryOptions?.result;
    const productAvailability = data.productAvailableQuantity?.result;
    const retailMulitplePickupMFeatureState = data?.featureState?.result?.find(
        (featureState: FeatureState) => featureState.Name === multiplePickupStoreSwitchName
    )?.IsEnabled;
    const channelDeliveryOptionConfig = data?.channelDeliveryOptionConfig?.result;

    const missingDimensions = product?.Dimensions?.filter(dimension => !dimension.DimensionValue?.Value);

    const {
        errorState: { configureErrors, quantityError, otherError, errorHost },
        modalOpen,
        quantity
    } = state;

    const { isSectionDisabled, buttonText } = getFindInStoreInventorySettings(
        props.context,
        props.context.app.config.enableStockCheck,
        productAvailability,
        props.resources,
        props.data.productAvailableQuantity.status === 'LOADING' || ArrayExtensions.hasElements(missingDimensions)
    );

    if (!product || !storeSelectorStateManager || !ArrayExtensions.hasElements(storeSelector)) {
        return undefined;
    }

    if (!deliveryOptions || !deliveryOptions.DeliveryOptions) {
        return undefined;
    }

    // If no delivery options present on the product, or none of the delivery options
    // match the PickupDeliveryModeCode, that means the item cannot be used for BOPIS
    if (retailMulitplePickupMFeatureState && channelDeliveryOptionConfig) {
        if (!matchDeliveryOptions(deliveryOptions.DeliveryOptions, channelDeliveryOptionConfig?.PickupDeliveryModeCodes)) {
            return undefined;
        }
    } else if (
        !PickupDeliveryModeCode ||
        !deliveryOptions.DeliveryOptions.find((option: DeliveryOption) => option.Code === PickupDeliveryModeCode)
    ) {
        return undefined;
    }

    const dialogStrings = {
        goToCartText: buyBoxGoToCartText,
        continueShoppingText: buyBoxContinueShoppingText,
        closeNotificationLabel,
        headerItemOneText: buyBoxSingleItemText,
        headerItemFormatText: buyBoxMultiLineItemFormatText,
        headerMessageText: buyBoxHeaderMessageText,
        freePriceText: priceFree,
        originalPriceText,
        currentPriceText
    };

    const priceComponent = price ? (
        <PriceComponent
            data={{ price }}
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            freePriceText={dialogStrings.freePriceText}
            originalPriceText={dialogStrings.originalPriceText}
            currentPriceText={dialogStrings.currentPriceText}
        />
    ) : (
        ''
    );

    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=240&h=240&m=6', w: 0, h: 0 },
            lg: { q: 'w=240&h=240&m=6', w: 0, h: 0 },
            xl: { q: 'w=240&h=240&m=6', w: 0, h: 0 }
        },
        lazyload: true
    };
    const popupProps: IPopupProps = {
        context: props.context,
        className: 'ms-buybox',
        id: props.id,
        typeName: props.typeName,
        data: { product: props.data.product?.result, price: props.data.productPrice?.result },
        dialogStrings,
        imageSettings: defaultImageSettings,
        gridSettings: props.context.request.gridSettings,
        productQuantity: quantity,
        priceComponent,
        navigationUrl: getUrlSync('cart', props.context.actionContext),
        modalOpen,
        setModalOpen: callbacks.changeModalOpen
    };
    const renderModalPopup = <Popup {...popupProps} />;

    return {
        ContainerProps: {
            className: 'ms-buybox__find-in-store'
        },

        storeSelector: storeSelector[0],

        openFindInStoreDialog: onClickFindInStoreHandler(
            props,
            state,
            callbacks,
            hideButton,
            customError,
            customStyleError,
            customColorError
        ),

        heading: (
            <div style={{ marginTop: '10px' }} className='add-to-cart-heading'>
                {/* <span className='title4'>{findInStoreHeaderText}</span> */}
                <span>{isSectionDisabled ? props.resources.pickUpNotAvailableHeader : props.resources.pickUpAvailableHeader}</span>
            </div>
        ),

        // description: <p className='ms-buybox__find-in-store-description'>{props.resources.pickUpAvailableHeader}</p>,
        productPickupOptionList:
            retailMulitplePickupMFeatureState && !isSectionDisabled ? (
                <>
                    <p className='ms-buybox__find-in-store-description'>{descriptionTextToShowAllPickupOptions}</p>
                    <ul className='ms-buybox__find-in-store-pickup' aria-label={descriptionTextToShowAllPickupOptions}>
                        {deliveryOptions.DeliveryOptions.map((item: DeliveryOption) => {
                            return (
                                <li className='ms-buybox__find-in-store-pickup-option' aria-label={item.Description} key={item.Code}>
                                    {item.Description}
                                </li>
                            );
                        })}
                    </ul>
                </>
            ) : null,
        errors: (
            <BuyboxErrorBlock
                configureErrors={configureErrors}
                quantityError={quantityError}
                otherError={otherError}
                resources={props.resources}
                showError={errorHost === 'FINDINSTORE'}
                isFindInStore={true}
            />
        ),

        button: (
            <Primary
                className='ms-buybox__find-in-store-button'
                // className={showAddToCart ? 'ms-buybox__find-in-store-button' : 'msc-add-to-cart'}
                onClick={
                    props.context.request.user.isAuthenticated
                        ? onClickFindInStoreHandler(
                              props,
                              state,
                              callbacks,
                              hideButton,
                              errorLabels,
                              customStyleError,
                              customColorError,
                              setValidationError,
                              setQuantityError
                          )
                        : () => {
                              window.location.replace(`${props.context.request.user.signInUrl}`);
                          }
                }
                ref={modalCloseButtonReference}
                aria-label={buttonText}
                disabled={cart === undefined || isSectionDisabled}
                // onMouseEnter={() => mouseEnterPickup()}
                // onMouseLeave={() => mouseLeavePickup()}
                style={{ minWidth: '215.84px', marginTop: '10px' }}
            >
                {props.context.request.user.isAuthenticated ? props.resources.pickUpAvailable : props.resources.pickUpAvailableSignIn}
            </Primary>
        ),

        modal: renderModalPopup,
        addToCartValidationError: (
            // @ts-ignore
            <Modal open={state.active}>
                <Error onClose={() => setValidationError(false)}>
                    <div style={{ marginTop: '3px' }}>{props.resources.addedToCartFailureMessage}</div>
                </Error>
            </Modal>
        ),
        addToCartQuantityError:
            // @ts-ignore
            state.quantityError ? (
                // @ts-ignore
                // <Modal open={state.quantityError}>
                <Error style={{ marginTop: '5px' }} onClose={() => setQuantityError(false)}>
                    <div style={{ marginTop: '3px' }}>
                        {quantityErrorMessage ? quantityErrorMessage : format(props.resources.maxQuantityError, max)}
                    </div>
                </Error>
            ) : // </Modal>
            null
    };
}

async function findInStoreOnClick(
    props: ILdsBuyboxProps<ILdsBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    hideButton: any,
    errorLabels?: any,
    customStyleError?: any,
    customColorError?: any,
    setValidationError?: any,
    setQuantityError?: any
): Promise<void> {
    const {
        data: {
            storeSelectorStateManager: { result: storeSelectorStateManager },
            cart: { result: cart },
            productAvailableQuantity: { result: productAvailableQuantity }
        },
        resources,
        context,
        modules
    } = props;

    const { selectedProduct, quantity } = state;

    let dataProduct = props.data.product.result;
    const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
    const retailMulitplePickupMFeatureState = props.data?.featureState?.result?.find(
        (featureState: FeatureState) => featureState.Name === multiplePickupStoreSwitchName
    )?.IsEnabled;

    if (selectedProduct) {
        dataProduct = (await selectedProduct) || dataProduct;
    }

    if (!dataProduct || !storeSelectorStateManager) {
        return;
    }

    const product = dataProduct;

    const missingDimensions =
        product.Dimensions &&
        product.Dimensions.filter((dimension: ProductDimension) => !(dimension.DimensionValue && dimension.DimensionValue.Value));

    let storeSelectorId: string = '';
    if (modules && Object.keys(modules).length > 0 && modules.storeSelector && modules.storeSelector.length > 0) {
        storeSelectorId = modules.storeSelector[0].id;
    }

    if (missingDimensions && missingDimensions.length > 0) {
        if (callbacks.updateErrorState) {
            callbacks.updateErrorState({
                errorHost: 'FINDINSTORE',
                configureErrors: getConfigureErrors(
                    missingDimensions,
                    resources,
                    undefined,
                    errorLabels,
                    customColorError,
                    customStyleError
                )
            });
        }
    } else {
        storeSelectorStateManager
            .openDialog({
                product,
                id: storeSelectorId,
                parentElementRef: modalCloseButtonReference,
                deliveryOptions: retailMulitplePickupMFeatureState ? props.data.deliveryOptions.result : undefined,
                onLocationSelected: async (orgUnitLocation: OrgUnitLocation, deliveryMode: string) => {
                    if (!cart) {
                        return Promise.resolve();
                    }

                    const behavior = props.context?.app?.config?.addToCartBehavior;

                    const cartState = await getCartState(props.context.actionContext);
                    const existingProduct = cartState.cart.CartLines?.find((cartLine: any) => {
                        return cartLine.ProductId === product.RecordId;
                    });
                    let copyQuantity = quantity;

                    const availableQuantity = productAvailableQuantity?.filter((store: any) => {
                        return store.InventLocationId === orgUnitLocation.InventoryLocationId;
                    });
                    if (existingProduct) {
                        const newQuantity = (copyQuantity += existingProduct.Quantity!);
                        if (
                            availableQuantity &&
                            availableQuantity.length > 0 &&
                            availableQuantity?.[0].ProductAvailableQuantity.AvailableQuantity &&
                            newQuantity > availableQuantity?.[0].ProductAvailableQuantity.AvailableQuantity
                        ) {
                            const failureResult: IAddToCartFailureResult = {
                                failureReason: 'CARTACTIONFAILED',
                                cartActionResult: { status: 'FAILED', substatus: 'MAXQUANTITY' }
                            };
                            if (availableQuantity?.[0].ProductAvailableQuantity.AvailableQuantity === 1) {
                                callbacks.updateErrorState({
                                    errorHost: 'ADDTOCART',
                                    configureErrors: {
                                        maxQuantityError: props.resources.errorMessageOutOfRangeOneLeft
                                    },
                                    otherError: getGenericError(
                                        failureResult,
                                        cart,
                                        resources,
                                        context,
                                        product,
                                        availableQuantity?.[0].ProductAvailableQuantity,
                                        orgUnitLocation
                                    )
                                });
                            } else {
                                callbacks.updateErrorState({
                                    errorHost: 'ADDTOCART',
                                    configureErrors: {
                                        maxQuantityError: props.resources.errorMessageOutOfRangeFormat.replace(
                                            '{numLeft}',
                                            availableQuantity?.[0].ProductAvailableQuantity.AvailableQuantity.toString()
                                        )
                                    },
                                    otherError: getGenericError(
                                        failureResult,
                                        cart,
                                        resources,
                                        context,
                                        product,
                                        availableQuantity?.[0].ProductAvailableQuantity,
                                        orgUnitLocation
                                    )
                                });
                            }
                        } else {
                            return cartState
                                .updateCartLineQuantity({
                                    cartLineId: existingProduct.LineId!,
                                    newQuantity: newQuantity,
                                    additionalProperties: {
                                        isUsingDefaultOrderSettingsMaxQuantity: true,
                                        location: orgUnitLocation,
                                        deliveryMode: deliveryMode
                                    }
                                })
                                .then((result: ICartActionResult) => {
                                    if (
                                        setValidationError &&
                                        result &&
                                        result.errorDetails &&
                                        result.errorDetails &&
                                        result.errorDetails.LocalizedMessage &&
                                        result.validationResults &&
                                        !result.validationResults[0]
                                    ) {
                                        if (
                                            result.errorDetails.LocalizedMessage === 'A validation error occurred.' ||
                                            result.errorDetails.LocalizedMessage === 'Error de validación.' ||
                                            result.errorDetails.LocalizedMessage === `Une erreur de contrôle s'est produite.` ||
                                            result.errorDetails.LocalizedMessage === `Errore di convalida.` ||
                                            result.errorDetails.LocalizedMessage === `유효성 검사 오류가 발생했습니다.` ||
                                            result.errorDetails.LocalizedMessage === `Erro na validação.` ||
                                            result.errorDetails.LocalizedMessage === `Произошла ошибка проверки.` ||
                                            result.errorDetails.LocalizedMessage === `Überprüfungsfehler` ||
                                            result.errorDetails.LocalizedMessage === `検証エラーが発生しました。`
                                        ) {
                                            setValidationError(true);
                                        }
                                    } else if (
                                        setQuantityError &&
                                        result &&
                                        result.errorDetails &&
                                        result.errorDetails &&
                                        result.errorDetails.LocalizedMessage &&
                                        result.validationResults &&
                                        result.validationResults[0] &&
                                        // @ts-ignore
                                        result.validationResults[0].LocalizedMessageParameters &&
                                        // @ts-ignore
                                        result.validationResults[0].LocalizedMessageParameters[0]
                                    ) {
                                        // setQuantityError(true, result.errorDetails.LocalizedMessage);
                                        setQuantityError(
                                            true,
                                            format(
                                                props.resources.maxQuantityError,
                                                // @ts-ignore
                                                result.validationResults[0].LocalizedMessageParameters[0]
                                            )
                                        );
                                    } else if (result.status === 'FAILED' || result.substatus === 'MAXQUANTITY') {
                                        callbacks.changeModalOpen(false);
                                        let productAvailability: ProductAvailableQuantity | undefined;
                                        if (state.productAvailableQuantity) {
                                            productAvailability = state.productAvailableQuantity.ProductAvailableQuantity;
                                        } else if (productAvailableQuantity && ArrayExtensions.hasElements(productAvailableQuantity)) {
                                            const shippingInventory = productAvailableQuantity.find(
                                                shipping => shipping.deliveryType === DeliveryMode.pickup
                                            );
                                            productAvailability = shippingInventory?.ProductAvailableQuantity;
                                        } else {
                                            productAvailability = undefined;
                                        }
                                        const failureResult: IAddToCartFailureResult = {
                                            failureReason: 'CARTACTIONFAILED',
                                            cartActionResult: { status: result.status, substatus: result.substatus }
                                        };
                                        callbacks.updateErrorState({
                                            errorHost: 'ADDTOCART',
                                            configureErrors: {},
                                            otherError: getGenericError(
                                                failureResult,
                                                cart,
                                                resources,
                                                context,
                                                product,
                                                productAvailability,
                                                orgUnitLocation
                                            )
                                        });
                                    } else {
                                        hideButton('addToCart');
                                        callbacks.updateErrorState({
                                            configureErrors: {}
                                        });
                                        const navigationUrl = getUrlSync('cart', context.actionContext);
                                        if (behavior === undefined || behavior === AddToCartBehavior.goToCart) {
                                            if (result.status === 'SUCCESS' && navigationUrl) {
                                                window.location.assign(navigationUrl);
                                            }
                                        } else if (behavior === AddToCartBehavior.showModal) {
                                            callbacks.changeModalOpen(true);
                                        } else if (behavior === AddToCartBehavior.showNotification) {
                                            const defaultImageSettings: IImageSettings = {
                                                viewports: {
                                                    xs: { q: 'w=240&h=240&m=6', w: 0, h: 0 },
                                                    lg: { q: 'w=240&h=240&m=6', w: 0, h: 0 },
                                                    xl: { q: 'w=240&h=240&m=6', w: 0, h: 0 }
                                                },
                                                lazyload: true
                                            };
                                            const telemetryContent = getTelemetryObject(
                                                props.context.request.telemetryPageName!,
                                                props.friendlyName,
                                                props.telemetry
                                            );
                                            const dialogStrings: IAddToCartResources = {
                                                goToCartText: resources.buyBoxGoToCartText,
                                                continueShoppingText: resources.buyBoxContinueShoppingText,
                                                closeNotificationLabel: resources.closeNotificationLabel,
                                                headerItemOneText: resources.buyBoxSingleItemText,
                                                headerItemFormatText: resources.buyBoxMultiLineItemFormatText,
                                                headerMessageText: resources.buyBoxHeaderMessageText,
                                                freePriceText: resources.priceFree,
                                                originalPriceText: resources.originalPriceText,
                                                currentPriceText: resources.currentPriceText,
                                                addedQuantityText: resources.addedQuantityText
                                            };
                                            const notification = new ItemSuccessfullyAddedToCartNotification(
                                                props.context,
                                                dialogStrings,
                                                defaultImageSettings,
                                                props.context.request.gridSettings,
                                                product,
                                                props.data.productPrice?.result,
                                                quantity,
                                                navigationUrl,
                                                telemetryContent,
                                                props.id,
                                                props.typeName
                                            );
                                            NotificationsManager.instance().addNotification(notification);
                                        }
                                    }
                                })
                                .catch((error: Error) => {
                                    if (props.telemetry) {
                                        props.telemetry.exception(error);
                                        props.telemetry.debug('Unable to add product to cart');
                                    }
                                });
                        }
                    } else {
                        if (
                            availableQuantity &&
                            availableQuantity.length > 0 &&
                            availableQuantity?.[0].ProductAvailableQuantity.AvailableQuantity &&
                            quantity > availableQuantity?.[0].ProductAvailableQuantity.AvailableQuantity
                        ) {
                            const failureResult: IAddToCartFailureResult = {
                                failureReason: 'CARTACTIONFAILED',
                                cartActionResult: { status: 'FAILED', substatus: 'MAXQUANTITY' }
                            };
                            if (availableQuantity?.[0].ProductAvailableQuantity.AvailableQuantity === 1) {
                                callbacks.updateErrorState({
                                    errorHost: 'ADDTOCART',
                                    configureErrors: {
                                        maxQuantityError: props.resources.errorMessageOutOfRangeOneLeft
                                    },
                                    otherError: getGenericError(
                                        failureResult,
                                        cart,
                                        resources,
                                        context,
                                        product,
                                        availableQuantity?.[0].ProductAvailableQuantity,
                                        orgUnitLocation
                                    )
                                });
                            } else {
                                callbacks.updateErrorState({
                                    errorHost: 'ADDTOCART',
                                    configureErrors: {
                                        maxQuantityError: props.resources.errorMessageOutOfRangeFormat.replace(
                                            '{numLeft}',
                                            availableQuantity?.[0].ProductAvailableQuantity.AvailableQuantity.toString()
                                        )
                                    },
                                    otherError: getGenericError(
                                        failureResult,
                                        cart,
                                        resources,
                                        context,
                                        product,
                                        availableQuantity?.[0].ProductAvailableQuantity,
                                        orgUnitLocation
                                    )
                                });
                            }
                        } else {
                            return cart
                                .addProductToCart({
                                    product,
                                    count: quantity,
                                    location: orgUnitLocation,
                                    deliveryMode
                                })
                                .then((result: ICartActionResult) => {
                                    if (
                                        setValidationError &&
                                        result &&
                                        result.errorDetails &&
                                        result.errorDetails &&
                                        result.errorDetails.LocalizedMessage &&
                                        result.validationResults &&
                                        !result.validationResults[0]
                                    ) {
                                        if (
                                            result.errorDetails.LocalizedMessage === 'A validation error occurred.' ||
                                            result.errorDetails.LocalizedMessage === 'Error de validación.' ||
                                            result.errorDetails.LocalizedMessage === `Une erreur de contrôle s'est produite.` ||
                                            result.errorDetails.LocalizedMessage === `Errore di convalida.` ||
                                            result.errorDetails.LocalizedMessage === `유효성 검사 오류가 발생했습니다.` ||
                                            result.errorDetails.LocalizedMessage === `Erro na validação.` ||
                                            result.errorDetails.LocalizedMessage === `Произошла ошибка проверки.` ||
                                            result.errorDetails.LocalizedMessage === `Überprüfungsfehler` ||
                                            result.errorDetails.LocalizedMessage === `検証エラーが発生しました。`
                                        ) {
                                            setValidationError(true);
                                        }
                                    } else if (
                                        setQuantityError &&
                                        result &&
                                        result.errorDetails &&
                                        result.errorDetails &&
                                        result.errorDetails.LocalizedMessage &&
                                        result.validationResults &&
                                        result.validationResults[0] &&
                                        // @ts-ignore
                                        result.validationResults[0].LocalizedMessageParameters &&
                                        // @ts-ignore
                                        result.validationResults[0].LocalizedMessageParameters[0]
                                    ) {
                                        // setQuantityError(true, result.errorDetails.LocalizedMessage);
                                        setQuantityError(
                                            true,
                                            format(
                                                props.resources.maxQuantityError,
                                                // @ts-ignore
                                                result.validationResults[0].LocalizedMessageParameters[0]
                                            )
                                        );
                                    } else if (result.status === 'FAILED' || result.substatus === 'MAXQUANTITY') {
                                        callbacks.changeModalOpen(false);
                                        let productAvailability: ProductAvailableQuantity | undefined;
                                        if (state.productAvailableQuantity) {
                                            productAvailability = state.productAvailableQuantity.ProductAvailableQuantity;
                                        } else if (productAvailableQuantity && ArrayExtensions.hasElements(productAvailableQuantity)) {
                                            const shippingInventory = productAvailableQuantity.find(
                                                shipping => shipping.deliveryType === DeliveryMode.pickup
                                            );
                                            productAvailability = shippingInventory?.ProductAvailableQuantity;
                                        } else {
                                            productAvailability = undefined;
                                        }
                                        const failureResult: IAddToCartFailureResult = {
                                            failureReason: 'CARTACTIONFAILED',
                                            cartActionResult: { status: result.status, substatus: result.substatus }
                                        };
                                        callbacks.updateErrorState({
                                            errorHost: 'ADDTOCART',
                                            configureErrors: {},
                                            otherError: getGenericError(
                                                failureResult,
                                                cart,
                                                resources,
                                                context,
                                                product,
                                                productAvailability,
                                                orgUnitLocation
                                            )
                                        });
                                    } else {
                                        hideButton('addToCart');
                                        callbacks.updateErrorState({
                                            configureErrors: {}
                                        });
                                        const navigationUrl = getUrlSync('cart', context.actionContext);
                                        if (behavior === undefined || behavior === AddToCartBehavior.goToCart) {
                                            if (result.status === 'SUCCESS' && navigationUrl) {
                                                window.location.assign(navigationUrl);
                                            }
                                        } else if (behavior === AddToCartBehavior.showModal) {
                                            callbacks.changeModalOpen(true);
                                        } else if (behavior === AddToCartBehavior.showNotification) {
                                            const defaultImageSettings: IImageSettings = {
                                                viewports: {
                                                    xs: { q: 'w=240&h=240&m=6', w: 0, h: 0 },
                                                    lg: { q: 'w=240&h=240&m=6', w: 0, h: 0 },
                                                    xl: { q: 'w=240&h=240&m=6', w: 0, h: 0 }
                                                },
                                                lazyload: true
                                            };
                                            const telemetryContent = getTelemetryObject(
                                                props.context.request.telemetryPageName!,
                                                props.friendlyName,
                                                props.telemetry
                                            );
                                            const dialogStrings: IAddToCartResources = {
                                                goToCartText: resources.buyBoxGoToCartText,
                                                continueShoppingText: resources.buyBoxContinueShoppingText,
                                                closeNotificationLabel: resources.closeNotificationLabel,
                                                headerItemOneText: resources.buyBoxSingleItemText,
                                                headerItemFormatText: resources.buyBoxMultiLineItemFormatText,
                                                headerMessageText: resources.buyBoxHeaderMessageText,
                                                freePriceText: resources.priceFree,
                                                originalPriceText: resources.originalPriceText,
                                                currentPriceText: resources.currentPriceText,
                                                addedQuantityText: resources.addedQuantityText
                                            };
                                            const notification = new ItemSuccessfullyAddedToCartNotification(
                                                props.context,
                                                dialogStrings,
                                                defaultImageSettings,
                                                props.context.request.gridSettings,
                                                product,
                                                props.data.productPrice?.result,
                                                quantity,
                                                navigationUrl,
                                                telemetryContent,
                                                props.id,
                                                props.typeName
                                            );
                                            NotificationsManager.instance().addNotification(notification);
                                        }
                                    }
                                })
                                .catch((error: Error) => {
                                    if (props.telemetry) {
                                        props.telemetry.exception(error);
                                        props.telemetry.debug('Unable to add product to cart');
                                    }
                                });
                        }
                    }
                }
            })
            .catch((error: Error) => {
                if (props.telemetry) {
                    props.telemetry.error(error.message);
                    props.telemetry.debug('Unable to find in store');
                }
            });
    }
}
